import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import CommonModal from "../../components/shared/modalLayout/CommonModal";
import InputWithLabel from "../../components/shared/fields/InputWithLabel";
import SelectBox from "../../components/shared/fields/SelectBox ";
import Button from "../../components/shared/buttons/button";
import useTranslator from "../../hooks/useTranslator";
import { useCreatePastingPackingMutation, useUpdatePastingPackingMutation } from "./mixingApi";
import { convertToUserTimezone, getCurrentDateTime } from "../../helpers/Utils";

import { SETTINGS } from "../../helpers/systemVariable";

const initialForm = {
  entered_date_time: getCurrentDateTime("YYYY-MM-DDTHH:mm"),
  batch_code:        "",
  shift:             "morning",
  total_boxes_packed:       "",
  weight_packed:     "",
  status:            "in_progress",
};

const AddPastingModal = ({ show, setShow, batchCode, record, mode = "add" }) => {
  const { _t } = useTranslator();
  const [formData, setFormData] = useState(initialForm);

  const STATUS_OPTIONS = [
    { value: "in_progress", label: _t("status_in_progress", "In Progress") },
    { value: "packed",      label: _t("status_packed",      "Packed")      },
  ];

  const SHIFT_OPTIONS = [
    { value: "morning", label: _t("morning", "Morning") },
    { value: "evening", label: _t("evening", "Evening") },
  ];

  const [createPastingPacking, { isLoading: isCreating }] = useCreatePastingPackingMutation();
  const [updatePastingPackingLog,     { isLoading: isUpdating }] = useUpdatePastingPackingMutation();

  const isView = mode === "view";

  // Populate form
  useEffect(() => {
    if (show) {
      if ((mode === "edit" || mode === "view") && record) {
        setFormData({
          entered_date_time: record.entered_date_time
            ? convertToUserTimezone(record.entered_date_time, "YYYY-MM-DDTHH:mm")
            : getCurrentDateTime("YYYY-MM-DDTHH:mm"),
          batch_code:    record.batch_code    || batchCode || "",
          shift:         record.shift         || "morning",
          total_boxes_packed:   record.total_boxes_packed   || "",
          weight_packed: record.weight_packed || "",
          status:        record.status        || "in_progress",
        });
      } else {
        setFormData({
          ...initialForm,
          entered_date_time: getCurrentDateTime("YYYY-MM-DDTHH:mm"),
          batch_code: batchCode ? String(batchCode) : "",
        });
      }
    }
  }, [show, record, mode, batchCode]);

  const handleClose = () => setShow(false);

  // Auto-calc weight_packed from total_boxes_packed
  useEffect(() => {
    if (isView) return;
    const boxes = parseFloat(formData.total_boxes_packed) || 0;
    setFormData((p) => ({
      ...p,
      weight_packed: boxes > 0 ? parseFloat((boxes * Number(SETTINGS.PASTING_PACKING_BOX_WEIGHT_IN_KG)).toFixed(2)) : "",
    }));
  }, [formData.total_boxes_packed]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isCreating || isUpdating) return;
    const payload = {
      batch_code:    batchCode ? String(batchCode) : formData.batch_code,
      shift:         formData.shift,
      total_boxes_packed:   parseFloat(formData.total_boxes_packed)   || 0,
      weight_packed: parseFloat(formData.weight_packed) || 0,
      status:        formData.status,
    };
    try {
      if (mode === "edit") {
        await updatePastingPackingLog({ id: record.id, requestBody: payload }).unwrap();
      } else {
        await createPastingPacking(payload).unwrap();
      }
      handleClose();
    } catch (err) {
      console.error("Error saving pasting packing:", err);
    }
  };

  return (
    <CommonModal
      show={show}
      handleClose={handleClose}
      setShow={setShow}
      title={
        isView
          ? _t("view_packing_material", "View Packing Material")
          : mode === "edit"
          ? _t("edit_packing_material", "Edit Packing Material")
          : _t("add_packing_material", "Add Packing Material")
      }
    >
      <Row>
        <Col lg={6}>
          <InputWithLabel
            label={_t("entered_date_time", "Entered Date Time")}
            type="datetime-local"
            name="entered_date_time"
            value={formData.entered_date_time}
            onChange={handleChange}
            disabled={true}
          />
        </Col>

        {/* <Col lg={6}>
          <InputWithLabel
            label={_t("batch_code", "Batch Code")}
            type="text"
            name="batch_code"
            value={formData.batch_code}
            disabled={true}
          />
        </Col> */}

        <Col lg={6}>
          <SelectBox
            label={_t("shift", "Shift")}
            name="shift"
            value={formData.shift}
            onChange={handleChange}
            options={SHIFT_OPTIONS}
            disabled={isView}
          />
        </Col>

        <Col lg={6}>
          <InputWithLabel
            label={_t("total_boxes_packed", "Total Boxes Packed")}
            type="number"
            name="total_boxes_packed"
            value={formData.total_boxes_packed}
            onChange={handleChange}
            placeholder="0"
            disabled={isView}
          />
        </Col>

        <Col lg={6}>
          <InputWithLabel
            label={_t("packed_weight", "Weight Packed")}
            type="number"
            name="weight_packed"
            value={formData.weight_packed}
            onChange={handleChange}
            placeholder="0"
            disabled={true}
          />
        </Col>

        <Col lg={6}>
          <SelectBox
            label={_t("status", "Status")}
            name="status"
            value={formData.status}
            onChange={handleChange}
            options={STATUS_OPTIONS}
            disabled={isView}
          />
        </Col>
      </Row>

      {!isView && (
        <Row>
          <Col className="text-end">
            <Button
              label={_t("save_changes", "Save Changes")}
              size="small"
              onClick={handleSubmit}
              disabled={isCreating || isUpdating}
            />
          </Col>
        </Row>
      )}
    </CommonModal>
  );
};

export default AddPastingModal;
